import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ISignUpPayload, IUserTokenPayload } from '@interfaces';
import { navToLogOut } from '@root/helpers/route-helpers';
import { EventQueueService } from '@services';
import { jwtDecode } from 'jwt-decode';
import { filter, map, ReplaySubject, tap, timer } from 'rxjs';
import { HttpRequestService } from './http-request.service';
import { TokenService } from './token.service';
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private tokenSource$ = new ReplaySubject<string>(1);
  public token$ = this.tokenSource$.asObservable();
  public tokenObject$ = this.token$.pipe(
    map((token) => {
      try {
        return jwtDecode<IUserTokenPayload>(token);
      } catch (err) {
        console.error('Invalid token', err);
        return null;
      }
    }),
  );

  // Clock that runs every minute
  timer = timer(0, 60000);

  constructor(
    private eventQueue: EventQueueService,
    private httpService: HttpRequestService,
    private tokenService: TokenService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.init();
  }

  private init() {
    this.registerSubs();

    setTimeout(() => {
      this.initAppWithAuth();
    });
  }

  private registerSubs() {
    this.registerApiTokenRefreshPushListener();
    this.registerTokenUpdatedListener();
    this.registerTokenTimerChecker();
    this.registerTokenQueryParamListener();
  }

  /**
   * Runs whenever the API needs to push a new token to the client
   */
  private registerApiTokenRefreshPushListener() {
    this.eventQueue
      .on('API_TOKEN_REFRESH_PUSH')
      .pipe(
        filter((_) => this.isAuthenticated),
        tap((newTokenString) => {
          this.storeToken(newTokenString);
        }),
      )
      .subscribe();
  }

  /**
   * Maintain the local subject value of the current token
   */
  private registerTokenUpdatedListener() {
    this.eventQueue
      .on('TOKEN_UPDATED')
      .pipe(
        filter((_) => this.isAuthenticated),
        tap((newTokenString) => {
          this.tokenSource$.next(newTokenString);
        }),
      )
      .subscribe();
  }

  /**
   * Check if the token is still valid on a timer and refresh it if it's not
   */
  private registerTokenTimerChecker() {
    this.timer
      .pipe(
        // Skip if not logged in
        filter((_) => this.isAuthenticated),
        tap((_) => this.assertTokenFreshness()),
      )
      .subscribe();
  }

  /**
   * Check if we have a token in the query params and use it if we do
   */
  private registerTokenQueryParamListener() {
    this.route.queryParams
      .pipe(
        filter((params) => !!params['token']),
        tap((params) => {
          // If we're already authenticated, we don't need to do anything
          if (this.isAuthenticated) return;
          const token = params['token'];
          this.storeToken(token);
          this.initAppWithAuth();
          // Do a default navigation to the home page
          this.router.navigate(['/'], {});
        }),
      )
      .subscribe();
  }

  public async logIn(payload: {
    email: string;
    password: string;
  }): Promise<boolean> {
    try {
      const res = await this.httpService.apiPost('login', payload, false);
      if (res.token) {
        this.storeToken(res.token);
        this.eventQueue.dispatch('LOG_IN_SUCCESSFUL', null);
        this.initAppWithAuth();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error logging in', error);
      throw error;
    }
  }

  private initAppWithAuth() {
    if (this.isAuthenticated && this.isEmailVerified) {
      this.eventQueue.dispatch('INIT_APP_WITH_AUTH', this.tokenObject);
    }
  }

  public logOut(): void {
    this.clearToken();
    this.eventQueue.dispatch('LOG_OUT_SUCCESSFUL', null);
    // THIS PART MUST FORCE TAB REFRESH OR NAVIGATE AWAY
    // STATE CLEANUP IS DONE BASED ON THAT
    navToLogOut(this.router);
  }

  public async signUp(payload: ISignUpPayload) {
    return this.httpService.apiPost('sign-up', payload, false);
  }

  /**
   * Get a new token from the API and replace the current one
   */
  public async refreshToken(initApp: boolean = false) {
    if (!this.isAuthenticated || this.isSingleRequestToken) {
      return false;
    }
    try {
      const res = await this.httpService.apiPost('refresh-token', {
        token: this.token,
      });
      if (res.token) {
        this.storeToken(res.token);
        this.eventQueue.dispatch('TOKEN_REFRESHED', this.tokenObject);
        if (initApp) {
          this.initAppWithAuth();
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error refreshing token', error);
      throw error;
    }
  }

  private assertTokenFreshness() {
    if (this.isAuthenticated) {
      const tokenExpiry = this.tokenObject?.exp;
      const now = Date.now() / 1000;
      const timeUntilExpiry = tokenExpiry - now;
      const allowedExpiry = 60 * 60 * 24; // 1 day
      if (timeUntilExpiry < allowedExpiry) {
        console.info('Token is about to expire, refreshing...');
        this.refreshToken();
      }
    }
  }

  /**
   * We extend common methods from token service to avoid extra injections
   * and to keep the token service as simple as possible.
   */
  private storeToken(token: string) {
    this.tokenService.storeToken(token);
  }

  private clearToken() {
    this.tokenService.clearToken();
  }

  public get token() {
    return this.tokenService.token;
  }

  public get tokenObject(): IUserTokenPayload | null {
    return this.tokenService.tokenObject;
  }

  public get isValidToken() {
    return this.tokenService.isValidToken;
  }

  public get isEmailVerified() {
    return this.tokenObject?.email_verified === true;
  }

  public get isAuthenticated() {
    return this.tokenObject?.exp > Date.now() / 1000;
  }

  public get isSingleRequestToken() {
    return this.tokenObject?.token_type === 'single_request_token';
  }
}
